import React, { useState } from "react";
import { ChevronLeftCircle, Edit, FileText } from "lucide-react";
import { OfflineOrderDetailsData } from "../../../Clinic/ClinicOrder/OfflineOrder/types";
import { EditOfflineOrderModal } from "./EditOfflineOrderModal";
import Bill from "../../../../../shared/Bill";
import { OfflineItemService } from "../../../../../services/api/OfflineOrder/offline.service";

interface AdminOfflineOrderDetailsProps {
  data: OfflineOrderDetailsData;
  setShowOfflineOrderDetails: React.Dispatch<React.SetStateAction<boolean>>;
  onOrderUpdated?: () => void;
}

export const AdminOfflineOrderDetails: React.FC<AdminOfflineOrderDetailsProps> = ({
  data: initialData,
  setShowOfflineOrderDetails,
  onOrderUpdated,
}) => {
  const [data, setData] = useState<OfflineOrderDetailsData>(initialData);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBillPreview, setShowBillPreview] = useState(false);
  const [activeBillOrderId, setActiveBillOrderId] = useState<number | string>(
    initialData.orderId
  );

  const handleRefreshOrderDetails = async (orderId: number | string) => {
    try {
      const response = await OfflineItemService.getOfflineOrderDetailsByOrderId(
        String(orderId)
      );
      if (response && response.success) {
        setData(response.data);
      }
      if (onOrderUpdated) {
        onOrderUpdated();
      }
      // Show updated bill preview as requested
      setActiveBillOrderId(orderId);
      setShowBillPreview(true);
    } catch (error) {
      console.error("Failed to refresh order details:", error);
    }
  };

  const handleOpenBill = () => {
    setActiveBillOrderId(data.orderId);
    setShowBillPreview(true);
  };

  const handleCloseBillPreview = () => {
    setShowBillPreview(false);
  };

  return (
    <>
      {/* Bill Preview Modal */}
      {showBillPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
          <div
            className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto relative shadow-2xl"
            style={{ scrollbarWidth: "thin" }}
          >
            <Bill
              orderId={Number(activeBillOrderId)}
              orderType="OFFLINE"
              onClose={handleCloseBillPreview}
            />
          </div>
        </div>
      )}

      {/* Edit Order Modal */}
      {showEditModal && (
        <EditOfflineOrderModal
          isOpen={showEditModal}
          order={data}
          onClose={() => setShowEditModal(false)}
          onSuccess={(orderId) => {
            handleRefreshOrderDetails(orderId);
          }}
        />
      )}

      {/* Navigation Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => setShowOfflineOrderDetails(false)}
          className="flex items-center gap-2 text-gray-700 hover:text-gray-900 font-medium transition-colors"
        >
          <ChevronLeftCircle size={22} />
          Back to Orders
        </button>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowEditModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors shadow-sm"
          >
            <Edit className="w-4 h-4" />
            Edit Order
          </button>
          <button
            onClick={handleOpenBill}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors shadow-sm"
          >
            <FileText className="w-4 h-4" />
            View Bill
          </button>
        </div>
      </div>

      <div className="w-full p-6 bg-white rounded-xl shadow-sm border border-gray-200">
        {/* Header Section */}
        <div className="bg-gray-50 p-4 rounded-lg mb-6 border border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-500 text-xs uppercase">Patient Name</span>
              <p className="text-gray-900 font-semibold mt-1">{data.patientName || "N/A"}</p>
            </div>
            <div>
              <span className="font-medium text-gray-500 text-xs uppercase">Mobile Number</span>
              <p className="text-gray-900 font-semibold mt-1">{data.patientMobileNumber || "N/A"}</p>
            </div>
            <div>
              <span className="font-medium text-gray-500 text-xs uppercase">Invoice Number</span>
              <p className="text-gray-900 font-semibold mt-1">{data.invoiceNumber || data.orderId}</p>
            </div>
            <div>
              <span className="font-medium text-gray-500 text-xs uppercase">Doctor Name</span>
              <p className="text-gray-900 font-semibold mt-1">{data.doctorName || "N/A"}</p>
            </div>
            <div>
              <span className="font-medium text-gray-500 text-xs uppercase">Clinic</span>
              <p className="text-gray-900 font-semibold mt-1">{data.clinicDetails?.clinicName || "N/A"}</p>
            </div>
            <div>
              <span className="font-medium text-gray-500 text-xs uppercase">Total Amount</span>
              <p className="text-green-700 font-bold mt-1">₹{data.totalAmount || "0.00"}</p>
            </div>
            <div>
              <span className="font-medium text-gray-500 text-xs uppercase">Total Items</span>
              <p className="text-gray-900 font-semibold mt-1">{data.totalItems || 0}</p>
            </div>
            <div>
              <span className="font-medium text-gray-500 text-xs uppercase">Order Date</span>
              <p className="text-gray-900 font-semibold mt-1">
                {data?.orderDate && !isNaN(new Date(data.orderDate).getTime())
                  ? new Date(data.orderDate).toLocaleDateString()
                  : "N/A"}
              </p>
            </div>
          </div>
        </div>

        {/* Product Details Section */}
        <div className="bg-white rounded-lg overflow-hidden border border-gray-200">
          <div className="text-gray-800 bg-gray-50 p-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-md font-bold text-gray-700 uppercase tracking-wide">
              PRODUCT DETAILS
            </h2>
            <span className="text-xs text-gray-500 font-medium">
              {data.productDetails?.length || 0} product(s) in this order
            </span>
          </div>

          {/* Table Header */}
          <div className="bg-cyan-50 border-b border-gray-200">
            <div className="grid grid-cols-11 gap-2 p-3 text-xs font-semibold text-gray-700">
              <div className="col-span-2">Product Name</div>
              <div className="text-center">Strips</div>
              <div className="text-center">Tablets</div>
              <div className="text-center">Pack</div>
              <div className="text-center">MRP</div>
              <div className="text-center">MRP/Unit</div>
              <div className="text-center">Discount</div>
              <div className="text-center">Price/Unit</div>
              <div className="text-center">Taxes (GST)</div>
              <div className="text-right">Total Price</div>
            </div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-gray-200">
            {data.productDetails?.map((product, index) => {
              const mrp = Number(product?.productMrp ?? 0);
              const packSize = Number(product.tabletsPerStripe || 1);
              const itemsCount = Number(product.productsItemsCount || 0);
              const discount = Number(product.sellingDiscount || 0);
              const unitPrice = mrp * (1 - discount / 100);

              return (
                <div
                  key={index}
                  className="grid grid-cols-11 gap-2 p-3 text-sm hover:bg-gray-50 items-center"
                >
                  <div className="col-span-2 flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0 border border-gray-200">
                      <img
                        src={product.productImage}
                        alt={product.productName}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src =
                            "https://via.placeholder.com/40";
                        }}
                      />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 line-clamp-1">
                        {product.productName}
                      </p>
                      {product.manufacturer && (
                        <p className="text-xs text-gray-500 line-clamp-1">
                          {product.manufacturer}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-center text-gray-900">
                    {Math.floor(itemsCount / packSize)} Strips
                  </div>
                  <div className="text-center text-gray-900">
                    {Math.floor(itemsCount % packSize)} Tablets
                  </div>
                  <div className="text-center text-gray-900">{packSize}</div>
                  <div className="text-center text-gray-900 font-medium">
                    ₹{mrp.toFixed(2)}
                  </div>
                  <div className="text-center text-gray-900">
                    ₹{(mrp / packSize).toFixed(2)}
                  </div>
                  <div className="text-center text-green-700 font-semibold">
                    {discount}%
                  </div>
                  <div className="text-center text-gray-900">
                    ₹{unitPrice.toFixed(2)}
                  </div>
                  <div className="text-center text-gray-600 text-xs">
                    CGST: {product.cgst || "0"}%
                    <br />
                    SGST: {product.sgst || "0"}%
                  </div>
                  <div className="text-right text-gray-900 font-bold">
                    ₹{Number(product?.totalPriceOfProduct || unitPrice * itemsCount).toFixed(2)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-end space-x-3 pt-6 mt-4 border-t border-gray-200">
          <button
            onClick={() => setShowEditModal(true)}
            className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-all duration-200 flex items-center gap-2 shadow-sm"
          >
            <Edit className="w-4 h-4" />
            Edit Product Price & Discount
          </button>
          <button
            onClick={handleOpenBill}
            className="px-6 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-all duration-200 flex items-center gap-2 shadow-sm"
          >
            <FileText className="w-4 h-4" />
            View Bill
          </button>
        </div>
      </div>
    </>
  );
};
export default AdminOfflineOrderDetails;
